import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useAuth } from '../context/AuthContext';
import { AtSign, X, Check, Loader2, Users } from 'lucide-react';
import { cn } from '../lib/utils';
import { API_BASE_URL as API } from '../config/api';



export function UserTagInput({ 
  value = [], 
  onChange, 
  maxTags = 10,
  placeholder = "Tag people..."
}) {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const searchTimeoutRef = useRef(null);

  // Debounced search
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 1 || !isAuthenticated) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${API}/api/tagging/search?q=${encodeURIComponent(query)}&limit=10`,
        { headers: getAuthHeaders() }
      );

      if (res.ok) {
        const data = await res.json();
        // Filter out already tagged users
        const taggedIds = value.map(u => u.user_id);
        const filtered = (data.users || []).filter(u => !taggedIds.includes(u.user_id));
        setSuggestions(filtered);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders, value]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (search) {
      searchTimeoutRef.current = setTimeout(() => {
        searchUsers(search);
      }, 300);
    } else {
      setSuggestions([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search, searchUsers]);

  const handleSelect = (user) => {
    if (value.length >= maxTags) return;
    
    onChange([...value, user]);
    setSearch('');
    setSuggestions([]);
  };

  const handleRemove = (userId) => {
    onChange(value.filter(u => u.user_id !== userId));
  };

  return (
    <div className="space-y-2">
      {/* Tagged Users Display */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map(user => (
            <Badge 
              key={user.user_id} 
              variant="secondary"
              className="flex items-center gap-1 pr-1"
            >
              <AtSign className="w-3 h-3" />
              {user.handle || user.name}
              <button
                type="button"
                onClick={() => handleRemove(user.user_id)}
                className="ml-1 hover:bg-muted/60 rounded p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Tag Input */}
      {value.length < maxTags && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              data-testid="tag-people-button"
            >
              <AtSign className="w-4 h-4 mr-2" />
              {placeholder}
              <span className="ml-auto text-xs">
                {value.length}/{maxTags}
              </span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0" align="start">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder="Search by name or @handle..."
                value={search}
                onValueChange={setSearch}
              />
              <CommandList>
                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                  </div>
                ) : suggestions.length === 0 ? (
                  <CommandEmpty>
                    {search ? 'No users found' : 'Start typing to search...'}
                  </CommandEmpty>
                ) : (
                  <CommandGroup>
                    {suggestions.map(user => (
                      <CommandItem
                        key={user.user_id}
                        value={user.user_id}
                        onSelect={() => handleSelect(user)}
                        className="flex items-center gap-3 cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-full bg-secondary/20 overflow-hidden flex items-center justify-center">
                          {user.picture ? (
                            <img src={user.picture} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-sm font-medium">
                              {(user.name || user.handle)?.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {user.name || `@${user.handle}`}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {user.handle ? `@${user.handle}` : ""}
                          </p>
                        </div>
                        {user.is_follower && (
                          <Badge variant="outline" className="text-xs shrink-0">
                            <Users className="w-3 h-3 mr-1" />
                            Follower
                          </Badge>
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}

      <p className="text-xs text-muted-foreground">
        You can tag people who follow you
      </p>
    </div>
  );
}

export default UserTagInput;
